import { useEffect, useMemo, useState } from 'react';
import { HardDrive, Monitor, Terminal, Tag, ChevronDown, ChevronRight } from 'lucide-react';
import { useImageCatalogStore } from '@/stores/useDiskImageStore';
import { usePoolStore } from '@/stores/usePoolStore';
import { formatRelativeTime } from '@/utils/format';
import { ImageCatalogEntry } from '@/types';
import clsx from 'clsx';

/** OS-themed gradient for image card header */
function getOsGradient(os: string): string {
  switch (os) {
    case 'windows':
      return 'from-blue-600 via-blue-500 to-cyan-400';
    case 'linux':
      return 'from-amber-600 via-orange-500 to-yellow-400';
    default:
      return 'from-surface-700 via-surface-600 to-surface-500';
  }
}

/** Render an image card */
function ImageCard({ image }: { image: ImageCatalogEntry }) {
  return (
    <div
      className={clsx(
        'rounded-fluent-lg overflow-hidden transition-all duration-200',
        'bg-white dark:bg-[#2a2a2a]',
        'border border-surface-200 dark:border-white/[0.06]',
        'hover:border-surface-300 dark:hover:border-white/[0.12]',
        'hover:shadow-elevation-8 hover:scale-[1.02]'
      )}
    >
      {/* Gradient header */}
      <div
        className={clsx(
          'h-16 bg-gradient-to-br relative overflow-hidden flex items-center justify-center',
          getOsGradient(image.os)
        )}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 40%)',
          }}
        />
        {image.os === 'windows' ? (
          <Monitor className="w-7 h-7 text-white/80 relative z-10" />
        ) : (
          <Terminal className="w-7 h-7 text-white/80 relative z-10" />
        )}
      </div>

      {/* Card body */}
      <div className="p-3.5">
        <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100 leading-snug truncate mb-1">
          {image.name}
        </h3>

        {image.description && (
          <p className="text-xs text-surface-500 dark:text-surface-400 mb-3 line-clamp-2">
            {image.description}
          </p>
        )}

        {image.tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            <Tag className="w-3 h-3 text-surface-400 flex-shrink-0" />
            {image.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-100 dark:bg-white/[0.06] text-surface-600 dark:text-surface-300"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-surface-100 dark:border-white/[0.06]">
          <span className="text-xs text-surface-500 dark:text-surface-400">
            Rev {image.latestRevision}
          </span>
          <span className="text-xs text-surface-400">
            {formatRelativeTime(image.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Render image card grid */
function ImageGrid({ images: items }: { images: ImageCatalogEntry[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((image) => (
        <ImageCard key={image.imageId} image={image} />
      ))}
    </div>
  );
}

export function DiskImagesPage() {
  const { images, isLoading, fetchImages } = useImageCatalogStore();
  const { selectedPoolIds, getSelectedPools, getPoolById } = usePoolStore();
  const selectedPools = getSelectedPools();
  const [collapsedPools, setCollapsedPools] = useState<Set<string>>(new Set());

  const isMultiPool = selectedPoolIds.length > 1;

  // Fetch images for all selected pools
  useEffect(() => {
    for (const poolId of selectedPoolIds) {
      fetchImages(poolId);
    }
  }, [selectedPoolIds, fetchImages]);

  // Flat list: all unique images across selected pools
  const allImages = useMemo(() => {
    const allImageIds = new Set(selectedPools.flatMap(p => p.imageIds));
    return images.filter(img => allImageIds.has(img.imageId));
  }, [selectedPools, images]);

  // Grouped by pool
  const groupedByPool = useMemo(() => {
    return selectedPoolIds.map(poolId => {
      const pool = getPoolById(poolId);
      if (!pool) return null;
      const poolImageIds = new Set(pool.imageIds);
      const poolImages = images.filter(img => poolImageIds.has(img.imageId));
      return { poolId, pool, images: poolImages };
    }).filter(Boolean) as { poolId: string; pool: NonNullable<ReturnType<typeof getPoolById>>; images: ImageCatalogEntry[] }[];
  }, [selectedPoolIds, images, getPoolById]);

  const toggleCollapse = (poolId: string) => {
    setCollapsedPools(prev => {
      const next = new Set(prev);
      if (next.has(poolId)) next.delete(poolId);
      else next.add(poolId);
      return next;
    });
  };

  const subtitle = (() => {
    if (selectedPools.length === 0) return 'Select a pool to view available images';
    if (selectedPools.length === 1) return `Available images for ${selectedPools[0].displayName} pool`;
    return `Available images across ${selectedPools.length} pools`;
  })();

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 animate-stagger-1">
        <h1 className="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Image Catalog
        </h1>
        <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
          {subtitle}
        </p>
      </div>

      {/* Content */}
      {isLoading && images.length === 0 ? (
        <div className="empty-state">
          <div className="spinner" />
          <p className="mt-3 text-sm text-surface-500">Loading images...</p>
        </div>
      ) : allImages.length === 0 ? (
        <div className="empty-state">
          <div className="dark:bg-white/[0.06] bg-surface-100 p-3 rounded-fluent-lg mb-3">
            <HardDrive className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100 mb-1">
            No images available
          </h3>
          <p className="text-sm text-surface-500 max-w-xs">
            {selectedPools.length > 0
              ? 'No images are configured for the selected pools.'
              : 'Select a pool to browse its image catalog.'}
          </p>
        </div>
      ) : isMultiPool ? (
        /* ─── Grouped by Pool ─── */
        <div className="space-y-5">
          {groupedByPool.map(({ poolId, pool, images: poolImages }) => {
            const isCollapsed = collapsedPools.has(poolId);
            return (
              <div key={poolId}>
                <button
                  onClick={() => toggleCollapse(poolId)}
                  className="flex items-center gap-2 mb-3 group"
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-surface-400 group-hover:text-surface-600 dark:group-hover:text-surface-300 transition-colors" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-surface-400 group-hover:text-surface-600 dark:group-hover:text-surface-300 transition-colors" />
                  )}
                  <span className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                    {pool.displayName}
                  </span>
                  <span className="text-xs text-surface-400 bg-surface-100 dark:bg-white/[0.06] rounded-full px-2 py-0.5">
                    {poolImages.length}
                  </span>
                </button>

                {!isCollapsed && (
                  poolImages.length === 0 ? (
                    <p className="text-sm text-surface-400 ml-6 mb-2">No images in this pool.</p>
                  ) : (
                    <ImageGrid images={poolImages} />
                  )
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ─── Flat grid (single pool) ─── */
        <ImageGrid images={allImages} />
      )}
    </div>
  );
}
